import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CountdownTimer } from './CountdownTimer';
import { CTAButton } from './CTAButton';
import { CheckIcon, PlusIcon, StarIcon, TrophyIcon } from 'lucide-react';
interface PricingOption {
  id: string;
  emoji: string;
  title: string;
  subtitle: string;
  oldPrice: string;
  price: string;
  badge?: string;
  isFeatured?: boolean;
  includes: {
    text: string;
    type: 'check' | 'bonus' | 'plus';
  }[];
  ctaText: string;
}
const options: PricingOption[] = [{
  id: 'base',
  emoji: '🛡️',
  title: 'SISTEMA BASE [ADMIN & LEGAL]',
  subtitle: 'La estructura mínima obligatoria para un consultorio blindado y eficiente.',
  oldPrice: '$49.990',
  price: '$19.990',
  includes: [{
    text: 'Módulo 1: Plantillas de Informes Anti-Rebote (CUD/Prepagas/Amparos).',
    type: 'check'
  }, {
    text: 'Módulo 2: Batería de Screening In-Situ (8 fichas de diagnóstico rápido).',
    type: 'check'
  }, {
    text: 'Módulo 3: Kit de Devolución para Padres (Guías completas TDAH/TEA).',
    type: 'check'
  }, {
    text: 'Módulo 4: Monitorización Data-Driven (6 planillas de seguimiento).',
    type: 'check'
  }, {
    text: 'Módulo 5: Blindaje Legal y Protocolos de Encuadre (Ley 26.529).',
    type: 'check'
  }, {
    text: 'BONO 1: Mega Kit Retención Clínica (Hoja de Ruta 30 días).',
    type: 'bonus'
  }, {
    text: 'BONO 2: Secretaria Digital (70 respuestas rápidas WhatsApp).',
    type: 'bonus'
  }],
  ctaText: 'QUIERO EL SISTEMA BASE'
}, {
  id: 'kids',
  emoji: '🧒',
  title: 'SISTEMA BASE + MEGA PACK TDAH CLÍNICO [KIDS 4-12]',
  subtitle: 'El estándar de oro para el tratamiento de sala. 6 meses de intervención ya planificados.',
  oldPrice: '$69.990',
  price: '$24.990',
  includes: [{
    text: 'TODO LO INCLUIDO EN EL SISTEMA BASE.',
    type: 'check'
  }, {
    text: '24 SESIONES ESTRUCTURADAS: Un semestre completo de intervención paso a paso (Entrás al consultorio sabiendo qué hacer hoy y el mes que viene).',
    type: 'plus'
  }, {
    text: 'BATERÍA COGNITIVA TDAH (120 ACTIVIDADES ESPECÍFICAS): 24 de Atención / 24 de Inhibición y Freno Motor / 24 de Planificación / 24 de Tensión Sostenida / 24 de Canalización de Energía.',
    type: 'plus'
  }, {
    text: 'BONO EXCLUSIVO: Banco de Actividades por Nivel de Energía (Elegís según cómo llegó el niño hoy).',
    type: 'plus'
  }, {
    text: 'BONO EXCLUSIVO: Protocolo "Sesión Desarmada" (Qué hacer ante crisis o bloqueos).',
    type: 'plus'
  }, {
    text: 'BONO EXCLUSIVO: Guía Psicoeducativa para Padres TDAH.',
    type: 'plus'
  }],
  ctaText: 'SUMAR PACK TDAH & KIDS'
}, {
  id: 'teens-geronto',
  emoji: '🎓🧠',
  title: 'SISTEMA BASE + ADOLESCENTE + ADULTO MAYOR',
  subtitle: 'Clínica Adolescente (12-18) + Geronto-Neuro (60-90). Dos extensiones en un solo acceso.',
  oldPrice: '$89.990',
  price: '$27.990',
  includes: [{
    text: 'TODO LO INCLUIDO EN EL SISTEMA BASE.',
    type: 'check'
  }, {
    text: '50 FICHAS COGNITIVAS ADOLESCENTES: Activación de Funciones Ejecutivas con estética adulta (sin dibujos infantiles).',
    type: 'plus'
  }, {
    text: 'GUÍA DE VINCULACIÓN TEEN: Cómo comunicar el diagnóstico a un adolescente y lograr adherencia sin generar rechazo.',
    type: 'plus'
  }, {
    text: 'PROTOCOLOS DE AUTONOMÍA: Material para trabajar la transición a la vida adulta y organización del estudio.',
    type: 'plus'
  }, {
    text: 'BATERÍA DE EVALUACIÓN 60-90: Fichas técnicas de despistaje rápido de memoria, lenguaje y orientación.',
    type: 'plus'
  }, {
    text: 'PACK DEMENCIA (LEVE/MODERADA): Hojas de mantenimiento cognitivo para entregar al cuidador post-diagnóstico.',
    type: 'plus'
  }, {
    text: 'PROTOCOLO DE APOYO A CUIDADORES: Guía de manejo de síntomas conductuales en el hogar.',
    type: 'plus'
  }],
  ctaText: 'SUMAR PACK ADOLESCENTE + ADULTO MAYOR'
}, {
  id: 'suite',
  emoji: '💎',
  title: 'NEURO-PRO SUITE COMPLETA',
  subtitle: 'Acceso TOTAL de por vida. El sistema definitivo para todas las etapas del ciclo vital.',
  oldPrice: '$84.990',
  price: '$34.990',
  badge: 'MÁS ELEGIDO',
  isFeatured: true,
  includes: [{
    text: 'SISTEMA BASE COMPLETO (Módulos 1 al 5 + Bonos).',
    type: 'check'
  }, {
    text: 'EXTENSIÓN PEDIÁTRICA & TDAH (24 sesiones + 120 actividades).',
    type: 'check'
  }, {
    text: 'EXTENSIÓN ADOLESCENTE (50 fichas + Guía Vincular).',
    type: 'check'
  }, {
    text: 'EXTENSIÓN ADULTO MAYOR (Evaluación + Mantenimiento Demencias).',
    type: 'check'
  }, {
    text: 'BONO EXCLUSIVO: Guía de Derivación Interdisciplinaria Segura (Blindaje Médico).',
    type: 'check'
  }],
  ctaText: '🏆 SÍ, QUIERO LA SUITE COMPLETA NEURO-PRO'
}];
export function Pricing() {
  const [selected, setSelected] = useState('suite');
  const selectedOption = options.find(o => o.id === selected)!;
  return <section id="compra" className="py-4 md:py-24 bg-[#0B1426] relative scroll-mt-10" data-id="element-165">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[600px] bg-blue-600/10 blur-[150px] rounded-full pointer-events-none" data-id="element-166"></div>

      <div className="max-w-3xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10" data-id="element-167">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-2 md:mb-10" data-id="element-168">
          <div className="inline-block bg-red-500/10 border border-red-500/20 rounded-lg md:rounded-xl p-1.5 md:p-4 mb-2 md:mb-8" data-id="element-169">
            <p className="text-red-400 font-bold text-[9px] md:text-sm uppercase tracking-wider mb-1 md:mb-3" data-id="element-170">
              ⏱️ OFERTA EXPIRA EN:
            </p>
            <CountdownTimer data-id="element-171" />
          </div>

          <h2 className="text-base md:text-4xl font-bold text-white mb-0.5 md:mb-2" data-id="element-172">
            🛒 SELECCIONÁ TU NIVEL{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600" data-id="element-173">
              NEURO-PRO
            </span>
          </h2>
          <p className="text-[10px] md:text-lg text-gray-400 mt-1 md:mt-3 hidden md:block" data-id="element-174">
            Todas las opciones incluyen descarga inmediata, acceso de por vida y
            actualizaciones 2025.
          </p>
        </motion.div>

        {/* Option Cards */}
        <div className="space-y-1.5 md:space-y-3 mb-2 md:mb-6" data-id="element-175">
          {options.map((option, index) => {
          const isSelected = selected === option.id;
          return <motion.div key={option.id} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.08
          }} onClick={() => setSelected(option.id)} className={`relative cursor-pointer rounded-xl md:rounded-2xl p-2 md:p-5 transition-all duration-300 border-2 ${isSelected ? option.isFeatured ? 'border-blue-400 bg-[#0d1f3c] shadow-[0_0_30px_rgba(37,99,235,0.2)]' : 'border-blue-400 bg-[#0d1f3c] shadow-[0_0_20px_rgba(37,99,235,0.15)]' : 'border-gray-800 bg-[#111827] hover:border-gray-700'}`} data-id="element-176">
                {option.badge && <span className="absolute -top-2.5 md:-top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[9px] md:text-xs font-bold px-2 md:px-3 py-0.5 rounded-full flex items-center gap-1 shadow-lg" data-id="element-177">
                    <StarIcon className="w-2.5 h-2.5 md:w-3 md:h-3 fill-current" data-id="element-178" />
                    {option.badge}
                  </span>}

                <div className="flex items-center gap-2 md:gap-4" data-id="element-179">
                  {/* Radio */}
                  <div className={`w-5 h-5 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'border-blue-400 bg-blue-500' : 'border-gray-600 bg-transparent'}`} data-id="element-180">
                    {isSelected && <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-white" data-id="element-181"></div>}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-182">
                    <div className="flex items-center gap-1.5 md:gap-2" data-id="element-183">
                      <span className="text-sm md:text-xl" data-id="element-184">{option.emoji}</span>
                      <h3 className="text-[11px] md:text-base font-bold text-white leading-tight truncate" data-id="element-185">
                        {option.title}
                      </h3>
                    </div>
                    <p className="text-[10px] md:text-xs text-gray-400 leading-snug line-clamp-1 md:line-clamp-2 hidden md:block" data-id="element-186">
                      {option.subtitle}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="text-right flex-shrink-0 ml-1 md:ml-2" data-id="element-187">
                    <p className="text-gray-500 line-through text-[10px] md:text-sm" data-id="element-188">
                      {option.oldPrice}
                    </p>
                    <p className={`text-base md:text-2xl font-extrabold ${isSelected ? 'text-blue-400' : 'text-white'}`} data-id="element-189">
                      {option.price}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="text-center mb-4 md:mb-6" data-id="element-190">
          <CTAButton text={selectedOption.ctaText + ' →'} isFinal={true} className="w-full !py-3 md:!py-5 !text-sm md:!text-xl" data-id="element-191" />
          <p className="text-gray-500 text-[10px] md:text-sm mt-2 md:mt-3 italic" data-id="element-192">
            Pago único · Acceso de por vida · Descarga inmediata
          </p>
        </motion.div>

        {/* Details of selected option */}
        <AnimatePresence mode="wait" data-id="element-193">
          <motion.div key={selected} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="bg-[#111827] border border-gray-800 rounded-2xl p-4 md:p-8" data-id="element-194">
            <h4 className="text-sm md:text-base font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-195">
              {selectedOption.title} INCLUYE:
            </h4>
            <ul className="space-y-3" data-id="element-196">
              {selectedOption.includes.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-197">
                  {item.type === 'check' && <span className="text-green-500 mt-0.5 flex-shrink-0" data-id="element-198">
                      ✅
                    </span>}
                  {item.type === 'bonus' && <span className="mt-0.5 flex-shrink-0" data-id="element-199">🎁</span>}
                  {item.type === 'plus' && <span className="text-blue-400 mt-0.5 flex-shrink-0 font-bold" data-id="element-200">
                      ➕
                    </span>}
                  <span className={`text-sm md:text-base leading-relaxed ${item.type === 'bonus' ? 'text-yellow-400 font-medium' : 'text-gray-300'}`} data-id="element-201">
                    {item.text}
                  </span>
                </li>)}
            </ul>

            {selectedOption.isFeatured && <div className="mt-6 pt-4 border-t border-gray-800 text-center" data-id="element-202">
                <p className="text-xs md:text-sm text-gray-500" data-id="element-203">
                  🔥{' '}
                  <span className="text-yellow-400 font-bold" data-id="element-204">
                    OFERTA LANZAMIENTO
                  </span>{' '}
                  — Ahorrás $50.000 comparado con la compra individual de los
                  módulos
                </p>
              </div>}
          </motion.div>
        </AnimatePresence>

        {/* Mercado Pago */}
        <div className="text-center mt-4 md:mt-6" data-id="element-205">
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/Mercado_Pago_logo.svg/2560px-Mercado_Pago_logo.svg.png" alt="Mercado Pago" className="h-5 md:h-8 mx-auto opacity-50 grayscale hover:grayscale-0 hover:opacity-70 transition-all" data-id="element-206" />
        </div>
      </div>
    </section>;
}